import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, FloatingActionBar, Input, Skeleton } from "@chef/components";
import { FILTER_CATEGORIES, BRAND_PATHS } from "@chef/constants";
import {
  getCutoffDateForWeek,
  getWeek,
  getYear,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import { useDebounce, useIntercom, usePrevious } from "@chef/hooks";
import { Search } from "@chef/icons/small";
import {
  BackToTopButton,
  CurrencyWrapper,
  ProductsFromCategoriesCardGrid,
  FilterNavigation,
} from "@chef/smart-components";
import {
  api,
  AppDispatch,
  IDeviationProduct,
  loadDefaultBasketAddonProducts,
  ProductsByCategoriesQuery,
  selectAddonProducts,
  storeWrapper,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import {
  filterAndSortProductsByCategoriesWithDate,
  getBasketFinances,
  getStandaloneProductCategoryIdsFromProductCategories,
  getDiscountedAmount,
} from "@chef/state-management/helpers";
import { isEmptyArray } from "@chef/utils/array";
import { isEqualStrings } from "@chef/utils/equal";
import { removeDuplicatesByKey } from "@chef/utils/removeDuplicates";
import { useStandaloneBasket, useTrack } from "@chef/state-management/hooks";

import { StandaloneBasketOverviewModal } from "../../../components/MySubscription/StandaloneBasketOverviewModal";

import { intl } from "./SubscriptionStandalonePage.Intl";

export const SubscriptionStandalonePage = () => {
  useIntercom(false);
  const { basket: addonProducts } = useStandaloneBasket({
    variant: "billing",
  });
  const track = useTrack();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const weekObj = nextDeliveryWeekDateObj();
  const week = getWeek(weekObj);
  const year = getYear(weekObj);

  useEffect(() => {
    dispatch(loadDefaultBasketAddonProducts());
  }, []);

  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const prevDebouncedSearchVale = usePrevious(debouncedSearchValue);

  useEffect(() => {
    if (
      debouncedSearchValue.length > 0 &&
      prevDebouncedSearchVale !== debouncedSearchValue
    ) {
      track("Product Searched", {
        affiliation: "Frontend process",
        query: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue, track, prevDebouncedSearchVale]);

  const {
    data: { productCategories } = {},
    isLoading: isLoadingProductCategories,
  } = useProductCategoriesQuery();

  const {
    data: { productsByCategories: standaloneProductsByCategories } = {},
    isLoading: isLoadingStandaloneProductsByCategories,
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategories },
  );

  const filteredStandaloneProductsByCategories = useMemo(() => {
    return filterAndSortProductsByCategoriesWithDate({
      productsByCategories: standaloneProductsByCategories || [],
      productCategories: productCategories || [],
      cutoffDate: getCutoffDateForWeek({ week, year }),
      shouldCheckIfSubscribable: true,
    });
  }, [standaloneProductsByCategories, productCategories, week, year]);

  const filtersWithDiscount = useMemo(() => {
    return generateFilterCategoriesFromProductsByCategories({
      productsByCategories: filteredStandaloneProductsByCategories,
      excludeDiscount: true,
      addonProducts,
    });
  }, [filteredStandaloneProductsByCategories]);

  const currentFilter =
    (router.query?.["filter"] as string) ||
    FILTER_CATEGORIES.ALL_CATEGORIES_SLUG;

  const isLoading =
    isLoadingStandaloneProductsByCategories || isLoadingProductCategories;

  const filteredProductsByCategories = filterProductsByCategoriesFilterCategory(
    {
      filter: currentFilter,
      productsByCategories: filteredStandaloneProductsByCategories,
      searchValue: debouncedSearchValue,
      yourPicks: addonProducts,
    },
  );

  const filteredProducts = filteredProductsByCategories.flatMap(
    (c) => c.products,
  );

  const updateUrl = (filter: string) => {
    if (filter === router.query?.["filter"]) {
      router.replace(
        {
          pathname: router.asPath.split("?")[0],
          query: { filter: FILTER_CATEGORIES.ALL_CATEGORIES_SLUG },
        },
        undefined,
        { scroll: false, shallow: true },
      );
      return;
    }
    router.replace(
      {
        pathname: router.asPath.split("?")[0],
        query: { filter },
      },
      undefined,
      { scroll: false, shallow: true },
    );
  };

  return (
    <div id="special-items-grid">
      <StandaloneBasketOverviewModal />

      <h1 className="hidden lg:block">{intl.ADD_GROCERIES}</h1>
      <p className="mt-2 text-sm text-center lg:text-start">
        <strong>{intl.WILL_BE_ADDED_TO_YOUR_SUBSCRIPTION}</strong>
      </p>

      <div className="mt-10 md:w-80">
        <Input
          type="search"
          Icon={Search}
          autoComplete="off"
          name="search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={intl.SEARCH}
        />
      </div>

      <div className="mt-8 mb-8 lg:mb-12">
        {isLoading && (
          <div className="flex w-full gap-2 -mr-8">
            <Skeleton height={9} width={32} />
            <Skeleton height={9} width={22} />
            <Skeleton height={9} width={32} />
          </div>
        )}

        {!isLoading && (
          <FilterNavigation
            items={filtersWithDiscount}
            value={currentFilter}
            onChange={updateUrl}
            resetFilters={() =>
              updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)
            }
            className="px-4 -mx-4 lg:mb-12 md:px-0 md:mx-0"
            leftClassName="bg-gradient-to-r from-background via-background"
            rightClassName="bg-gradient-to-l from-background via-background"
          />
        )}
      </div>

      {isEmptyArray(filteredProducts) && isEmptyArray(addonProducts) && (
        <p>{intl.COULD_NOT_FIND_ANY}</p>
      )}

      {filteredProductsByCategories.map((category) => {
        if (category.products.length === 0) {
          return null;
        }
        return (
          <div
            key={category.productCategoryId}
            id={`${category.productCategoryId}-card`}
          >
            <h2>{category.productCategoryName}</h2>
            <ProductsFromCategoriesCardGrid
              products={category.products}
              categoryId={category.productCategoryId}
              view="deviation"
              basketType="billing"
              week={week}
              year={year}
            />
          </div>
        );
      })}
      <BackToTopButton />
    </div>
  );
};

export const SubscriptionStandalonePageGetStaticProps =
  storeWrapper.getStaticProps((store) => async () => {
    const weekObj = nextDeliveryWeekDateObj();
    const week = getWeek(weekObj);
    const year = getYear(weekObj);

    const result = await store
      .dispatch(api.endpoints.productCategories.initiate())
      .unwrap();

    await store.dispatch(
      api.endpoints.productsByCategories.initiate({
        categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
          result.productCategories,
        ),
        week,
        year,
      }),
    );

    return {
      props: {},
      revalidate: 1800,
    };
  });

export const SubscriptionStandaloneActionBar = () => {
  const router = useRouter();
  const track = useTrack();
  useEffect(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Add addons",
      url: window.location.href,
    });
  }, [track]);

  const basketProducts = useSelector(selectAddonProducts);
  const price = getBasketFinances({
    basketProducts: basketProducts || [],
  }).addon;

  const handleGoNext = () => {
    track("updateSubscriptionStepCompleted", {
      affiliation: "Frontend process",
      changed_subscription_details: "Add addons",
      url: window.location.href,
    });

    router.push(BRAND_PATHS.ADDONS_SUBSCRIPTION_CONFIRMATION_PAGE);
  };

  return (
    <FloatingActionBar>
      <div
        className="col-span-1 col-start-1 md:col-start-9 md:text-right"
        id="total-price"
      >
        <strong>
          <p className="text-sm underline">{intl.TOTAL}</p>
          <div>
            <CurrencyWrapper>{price}</CurrencyWrapper>
          </div>
        </strong>
      </div>

      <Button
        primary
        className="col-span-3 col-start-10 no-underline"
        id="floating-editor-bar-btn"
        onClick={handleGoNext}
      >
        {intl.CONTINUE_BUTTON}
      </Button>
    </FloatingActionBar>
  );
};

export const filterProductsByCategoriesFilterCategory = (args: {
  productsByCategories: ProductsByCategoriesQuery["productsByCategories"];
  filter: string;
  searchValue?: string;
  yourPicks: IDeviationProduct[];
}) => {
  const { productsByCategories, filter, searchValue, yourPicks } = args;

  if (searchValue) {
    return productsByCategories.map((pbc) => ({
      ...pbc,
      products: pbc.products.filter((p) =>
        p.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    }));
  } else {
    if (filter === FILTER_CATEGORIES.ALL_CATEGORIES_SLUG) {
      if (yourPicks.length > 0) {
        return [
          createYourPicks({ yourPicks, productsByCategories }),
          ...productsByCategories,
        ];
      } else {
        return productsByCategories;
      }
    }

    if (filter === FILTER_CATEGORIES.YOUR_PICKS_SLUG) {
      return [createYourPicks({ yourPicks, productsByCategories })];
    }

    const newProductsByCategories: ProductsByCategoriesQuery["productsByCategories"] =
      [];

    for (const pbc of productsByCategories) {
      if (filter === FILTER_CATEGORIES.OFFERS_SLUG) {
        const products = pbc.products.filter((p) =>
          isProductByCategoriesOnDiscount({
            productsByCategory: pbc,
            productId: p.productId,
          }),
        );
        if (products.length === 0) {
          continue;
        }

        newProductsByCategories.push({ ...pbc, products });
        continue;
      }

      if (pbc.slug === filter) {
        newProductsByCategories.push(pbc);
      }
    }

    return newProductsByCategories;
  }
};

const createYourPicks = ({
  yourPicks,
  productsByCategories,
}: {
  yourPicks: IDeviationProduct[];
  productsByCategories: ProductsByCategoriesQuery["productsByCategories"];
}) => {
  const allProducts = productsByCategories.flatMap((pbc) => pbc.products);

  // Not really sure why we need to remove duplicates here
  const products = removeDuplicatesByKey(
    allProducts.filter((product) =>
      product.variations.find((v) =>
        yourPicks.find((yp) => yp.variationId === v.variationId),
      ),
    ),
    "productId",
  );

  return {
    productCategoryName: FILTER_CATEGORIES.YOUR_PICKS,
    productCategoryId: FILTER_CATEGORIES.YOUR_PICKS_SLUG,
    products,
  };
};

interface IFilterCategory {
  value: string;
  label: string;
  count: number;
  type: "default" | "highlighted" | "secondary";
  isAll: boolean;
}
export const generateFilterCategoriesFromProductsByCategories = (args: {
  productsByCategories: ProductsByCategoriesQuery["productsByCategories"];
  excludeAll?: boolean;
  excludeDiscount?: boolean;
  addonProducts: IDeviationProduct[];
}) => {
  const { productsByCategories, excludeAll, excludeDiscount, addonProducts } =
    args;

  const generatedCategories: IFilterCategory[] = productsByCategories.map(
    (pbc) => ({
      value: pbc.slug,
      label: pbc.productCategoryName,
      count: pbc.products.length,
      type: "highlighted",
      isAll: false,
    }),
  );

  if (!excludeDiscount) {
    const discountedProductCount = productsByCategories.reduce(
      (a, b) =>
        a +
        b.products.filter((p) =>
          isProductByCategoriesOnDiscount({
            productsByCategory: b,
            productId: p.productId,
          }),
        ).length,
      0,
    );

    // only add if we have discounted products
    if (discountedProductCount) {
      generatedCategories.unshift({
        value: FILTER_CATEGORIES.OFFERS_SLUG,
        label: FILTER_CATEGORIES.OFFERS,
        count: discountedProductCount,
        type: "highlighted",
        isAll: false,
      });
    }
  }

  if (!excludeAll) {
    generatedCategories.unshift({
      value: FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
      label: FILTER_CATEGORIES.ALL_CATEGORIES,
      count: productsByCategories.reduce((a, b) => a + b.products.length, 0),
      type: "default",
      isAll: true,
    });
  }

  if (addonProducts.length > 0) {
    generatedCategories.unshift({
      value: FILTER_CATEGORIES.YOUR_PICKS_SLUG,
      label: FILTER_CATEGORIES.YOUR_PICKS,
      count: addonProducts.length,
      type: "highlighted",
      isAll: false,
    });
  }

  return generatedCategories;
};

export const isProductByCategoriesOnDiscount = (args: {
  productsByCategory: ProductsByCategoriesQuery["productsByCategories"][0];
  productId: string;
}): boolean => {
  const { productsByCategory, productId } = args;

  const product = productsByCategory.products.find((p) =>
    isEqualStrings(p.productId, productId),
  );
  if (!product) {
    return false;
  }

  const variation = product.variations[0];
  return !!getDiscountedAmount(variation);
};
